import {
  Box,
  CircularProgress,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material'
import React, { FC, useState } from 'react'
import { Yakureki, YakurekiDetail } from '@/shared/types/yakurekiCore'
import MuiAlert from '@mui/material/Alert'

type Props = {
  reloadPatientDetail: () => void
  yakureki: Yakureki
  /**
   * 選択中の患者と本 toast の yakureki が一致する時だけ渡される YakurekiDetail.
   * status 判定で patientList より優先。streaming 経路で patientList が
   * PROCESSING を取りこぼす race の回避用 (patientDetail は単一レコード
   * snapshot で PROCESSING を確実に拾える).
   */
  patientDetail: YakurekiDetail | null
  index: number
  isSending: boolean
}

// legacy app 専用 SOAP 生成 progress toast (= 旧 Material 流 filled MuiAlert +
// CircularProgress). Notion 黒トーストとは別系統の自前 Snackbar
// (ToastContext.showToast 経由ではない). legacy 階層内で完結.
const ALERT_PROPS = { elevation: 6, variant: 'filled' as const }

const SUCCESS_SX = {
  backgroundColor: 'rgba(76, 175, 80, 0.85)',
  border: '1px solid rgba(76, 175, 80, 0.5)',
  '& .MuiAlert-icon': { color: 'white' },
}

const ERROR_SX = {
  backgroundColor: 'rgba(208, 0, 0, 0.85)',
  border: '1px solid rgba(208, 0, 0, 0.5)',
  '& .MuiAlert-icon': { color: 'white' },
}

const PROGRESS_CONTAINER_SX = {
  bgcolor: 'rgba(97, 97, 97, 0.9)',
  border: '1px solid rgba(97, 97, 97, 0.5)',
  borderRadius: 1,
  px: 2.5,
  py: 1.5,
  alignItems: 'center',
  justifyContent: 'center',
  width: 220,
}

const MESSAGE_SX = { color: 'white', fontWeight: 500 }

/**
 * SOAP 生成中 / 完了 / 失敗の toast 通知。
 *
 * patientList の各要素に対して 1 つ render され、各 yakureki の status 遷移
 * (processing → completed/failed) を監視して対応する Snackbar を出す。
 * legacy (batch) / streaming の両経路で共通。mode 判定は本 component 内で
 * 行わず、caller が patientList 全体に対して map で render する.
 *
 * 選択中患者については `patientDetail.status` を優先する。これは streaming
 * 経路で `patientList` (delta sync) が PROCESSING を取りこぼす race の対策。
 */
export const SoapLoadingToast: FC<Props> = ({
  reloadPatientDetail,
  yakureki,
  patientDetail,
  isSending,
}) => {
  const [isCompleted, setIsCompleted] = useState<boolean>(false)
  const [isFailed, setIsFailed] = useState<boolean>(false)

  // 選択中患者の場合は patientDetail.status を優先、それ以外は patientList 由来
  const effectiveStatus = patientDetail?.status ?? yakureki.status
  const currentIsProgress = effectiveStatus === 'processing'
  const currentIsFail =
    effectiveStatus === 'failed' || effectiveStatus === 'failed_short'

  // 前回の状態を保持（レンダリング中の比較用）
  const [prevIsProgress, setPrevIsProgress] = useState(currentIsProgress)
  const [prevIsFail, setPrevIsFail] = useState(currentIsFail)

  // レンダリング中に状態の変化を検出
  // 進行中から完了への遷移を検出して、適切なトーストを表示
  if (currentIsProgress !== prevIsProgress) {
    const wasInProgress = prevIsProgress
    const isNowComplete = !currentIsProgress

    if (wasInProgress && isNowComplete) {
      const wasFailed = prevIsFail
      const isNowFailed = currentIsFail

      if (!wasFailed && isNowFailed) {
        setIsFailed(true)
        // 非同期処理は useEffect で実行
        Promise.resolve().then(() => reloadPatientDetail())
      } else {
        setIsCompleted(true)
        // 非同期処理は useEffect で実行
        Promise.resolve().then(() => reloadPatientDetail())
      }
    }

    setPrevIsProgress(currentIsProgress)
  }

  if (currentIsFail !== prevIsFail) {
    setPrevIsFail(currentIsFail)
  }

  const SendingSuccessToast = (
    <Snackbar
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      open={isCompleted}
      onClose={() => {
        setIsCompleted(false)
      }}
      autoHideDuration={3000}
    >
      {/* Box でラップ: Snackbar の Grow が direction prop を渡すため (MUI Issue #35649) */}
      <Box>
        <MuiAlert
          {...ALERT_PROPS}
          severity="success"
          onClose={() => {
            setIsCompleted(false)
          }}
          sx={SUCCESS_SX}
        >
          <Typography sx={MESSAGE_SX}>
            {yakureki.label}さんのSOAPを生成しました
          </Typography>
        </MuiAlert>
      </Box>
    </Snackbar>
  )

  const SendingFailToast = (
    <Snackbar
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      open={isFailed}
      onClose={() => {
        setIsFailed(false)
      }}
      autoHideDuration={3000}
    >
      <Box>
        <MuiAlert
          {...ALERT_PROPS}
          severity="error"
          onClose={() => {
            setIsFailed(false)
          }}
          sx={ERROR_SX}
        >
          <Typography sx={MESSAGE_SX}>
            {yakureki.label}さんのSOAP生成に失敗しました
          </Typography>
        </MuiAlert>
      </Box>
    </Snackbar>
  )

  const LoadingOverlay = (
    <Snackbar
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      open={isSending || currentIsProgress}
    >
      {/* Box でラップ: Snackbar の Grow トランジションが direction prop を子に渡すため、
          Stack を直接配置すると prop 競合で警告が出る (MUI Issue #35649) */}
      <Box>
        <Stack direction="row" spacing={1.5} sx={PROGRESS_CONTAINER_SX}>
          <CircularProgress size={20} sx={{ color: 'white', flexShrink: 0 }} />
          <Typography sx={{ ...MESSAGE_SX, whiteSpace: 'nowrap' }}>
            {isSending ? '音声をアップロード中' : 'SOAP生成中'}
          </Typography>
        </Stack>
      </Box>
    </Snackbar>
  )
  return (
    <>
      {SendingSuccessToast}
      {LoadingOverlay}
      {SendingFailToast}
    </>
  )
}
